/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { Button, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IOrderTemplateLineViewModel } from '../order-template';
import { IOrderTemplateData } from '../order-template.data';
import { IOrderTemplateProps } from '../order-template.props.autogenerated';
import { AddLineToTemplate } from '.';
import { ConfirmAddedToCartDialog } from '../components/add-to-cart-confirmation-dialog-component';
// import { IFullProduct } from '../../../themes/adventureworks/views/common/order-templates';
// import { IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import { format } from '@msdyn365-commerce/retail-proxy';

interface IOrderTemplateBulkActionsProps extends IOrderTemplateProps<IOrderTemplateData> {
    orderTemplateId: string;
    selectedLines: IOrderTemplateLineViewModel[];
    isMinifiedView: boolean;
    isMultipleCatalogFeatureEnabledInHq: boolean;
    telemetryContent?: ITelemetryContent;
    onAddSelectionToBag(conset: boolean): Promise<void>;
    onRemoveSelection(): void;
}

interface IOrderTemplateBulkActionsState {
    isAddDisabled: boolean;
    isRemoveDisabled: boolean;
    isPopUpOpen?: boolean;
    isAlternateItems?: boolean;
    confirmHeaderText?: string;
    confirmBodyText?: string;
}

/**
 * Bulk Order Template line actions.
 */
export class OrderTemplateBulkActions extends React.PureComponent<IOrderTemplateBulkActionsProps, IOrderTemplateBulkActionsState> {

    public state: IOrderTemplateBulkActionsState = {
        isAddDisabled: true,
        isRemoveDisabled: true,
        isPopUpOpen: false,
        isAlternateItems: false,
        confirmBodyText: '',
        confirmHeaderText: ''
    };

    public static getDerivedStateFromProps(props: IOrderTemplateBulkActionsProps,
        state: IOrderTemplateBulkActionsState): IOrderTemplateBulkActionsState {
        const selectedLines = props.selectedLines.length;
        let nextState = { ...state };
        nextState = {
            isAddDisabled: selectedLines === 0,
            isRemoveDisabled: selectedLines === 0
        };
        return nextState;
    }

    public render(): JSX.Element {
        const { addLineModalLinkText,
            searchButtonAriaLabel,
            searchInputAriaLabel,
            searchModalPlaceholderText,
			selectCatalogTitleText,
            selectProductButtonText,
            addItemToTemplateText,
            addLineProductUnitPricePrefix,
            backButtonText,
            decrementButtonAriaLabel,
            incrementButtonAriaLabel,
            quantitySelectLabel,
            addLineProductUnitOfMeasurePrefix,
            notFoundSearchErrorNotice,
            notFoundSearchErrorRedediation,
            searchErrorMessage,
            productDimensionTypeColor,
            productDimensionTypeConfiguration,
            productDimensionTypeSize,
            productDimensionTypeStyle,
            addSelectedToBagButtonText,
            removeSelectedToBagButtonText,
            searchResultsCountVerbage,
            searchResultsCountSubject,
            addToTemplateConfirmation,
            totalPriceLabel,
            progressNotificationText,
            addToTemplateDuplicateError,
            addToTemplateGenericError,
            dimensionMissingError
        } = this.props.resources;
        const addLineProps = {
            context: this.props.context,
			telemetryContent: this.props.telemetryContent,
            resources: {
                addLineModalLinkText,
                searchButtonAriaLabel,
                searchInputAriaLabel,
                searchModalPlaceholderText,
				selectCatalogTitleText,
                selectProductButtonText,
                addItemToTemplateText,
                addLineProductUnitPricePrefix,
                backButtonText,
                decrementButtonAriaLabel,
                incrementButtonAriaLabel,
                quantitySelectLabel,
                addLineProductUnitOfMeasurePrefix,
                notFoundSearchErrorNotice,
                notFoundSearchErrorRedediation,
                searchErrorMessage,
                productDimensionTypeColor,
                productDimensionTypeConfiguration,
                productDimensionTypeSize,
                productDimensionTypeStyle,
                searchResultsCountVerbage,
                searchResultsCountSubject,
                addToTemplateConfirmation,
                totalPriceLabel,
                progressNotificationText,
                addToTemplateDuplicateError,
                addToTemplateGenericError,
                dimensionMissingError
            },
            imageSettings: this.props.config.imageSettings,
            orderTemplateId: this.props.orderTemplateId,
            isMultipleCatalogFeatureEnabledInHq: this.props.isMultipleCatalogFeatureEnabledInHq,
            catalogs: this.props.data.catalogs?.result
        };

        return (
            <>
                <AddLineToTemplate {...addLineProps} />
                <Button
                    onClick={this.addHandler}
                    disabled={this.state.isAddDisabled}
                    className='add-selected-to-bag'
                    aria-label={addSelectedToBagButtonText}
                >
                    <span />
                    {!this.props.isMinifiedView && addSelectedToBagButtonText}
                </Button>
                <Button
                    onClick={this.removeHandler}
                    disabled={this.state.isRemoveDisabled}
                    className='remove-selected'
                    aria-label={removeSelectedToBagButtonText}
                >
                    <span />
                    {!this.props.isMinifiedView && removeSelectedToBagButtonText}
                </Button>
                    <ConfirmAddedToCartDialog
                className='msc-confirm-added-to-cart'
                modalHeader={this.state.confirmHeaderText??''}
                modalBodyText={this.state.confirmBodyText??''}
                modalOpen={this.state.isPopUpOpen}
                onClose={this.onCloseClick}
                alternateItem={this.state.isAlternateItems??false}
                _addItemToCartCustom={this.onConfirmYesClick}
                quantity={1}
                onNoClick={this.onConfirmNoClick}
                />
            </>
        );
    }

    public onCloseClick = () =>{
        this.setState({isPopUpOpen: false});
    };

    public onConfirmYesClick = () =>{
        this.setState({isPopUpOpen: false});
        void this.addHandlerCustom();
    };

    public onConfirmNoClick =()=>{
        this.setState({isPopUpOpen: false});
        void this.addHandlerCustom();
    };

    private readonly addHandlerCustom = async () => {
        this.setState({
            isAddDisabled: true
        });

       this.props.onAddSelectionToBag(false);
        const selItems = this.props.selectedLines.filter(line => (line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct.AlternateProduct.currentProd && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always'));
            if(selItems && selItems.length > 0)
            {
                const supersedeItems = format(this.props.config.alternateItemPopUpBody??'',selItems.map(x => `${x.fullProduct?.ProductDetails.ItemId  } - ${  x.fullProduct?.AlternateProduct?.currentProd?.productDtl?.ItemId}`).join(', '));
                this.setState({isPopUpOpen: true,confirmBodyText: supersedeItems,confirmHeaderText: this.props.config.alternateItemPopUpHeader,isAlternateItems:true});
            }

        this.setState({
            isAddDisabled: false
        });
    };

    private readonly addHandler = async (event: React.MouseEvent<HTMLElement>) => {
        // const gcBackOrderAllowed = this.props.data.customerInformation.result && this.props.data.customerInformation.result.ExtensionProperties ? this.props.data.customerInformation.result.ExtensionProperties?.find(f=>f.Key==="GCALLOWBACKORDERS")?.Value?.StringValue : '';
        // const backOrderProduct = this.props.selectedLines.find(line => this._inventoryLabel(line.fullProduct, line.productAvailability) === 'Out of stock' && (!line.fullProduct?.AlternateProduct?.serviceResp || !line.fullProduct.AlternateProduct.currentProd || (line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId !== 'Always')));
        // if (backOrderProduct && gcBackOrderAllowed !== '2') {
        //     backOrderProduct.fullProduct?.ProductDetails.ExtensionProperties?.push({Key:'IsBackOrder', Value:{BooleanValue: true} });
        //     this.setState({ isPopUpOpen: true,confirmHeaderText: this.props.config.confirmationPopUpHeader,confirmBodyText: this.props.config.confirmationPopUpBody,isAlternateItems:false });
        // }
        // else
        // {
            this.setState({
                isAddDisabled: true
            });
            this.props.onAddSelectionToBag(false);
            const selItems = this.props.selectedLines.filter(line => (line.fullProduct?.AlternateProduct?.serviceResp && line.fullProduct.AlternateProduct.currentProd && line.fullProduct?.AlternateProduct?.serviceResp[0].UseAltItemId === 'Always'));
            if(selItems && selItems.length > 0)
            {
                const supersedeItems = format(this.props.config.alternateItemPopUpBody??'',selItems.map(x => `${x.fullProduct?.ProductDetails.ItemId  } - ${  x.fullProduct?.AlternateProduct?.currentProd?.productDtl?.ItemId}`).join(', '));
                this.setState({isPopUpOpen: true,confirmBodyText: supersedeItems,confirmHeaderText: this.props.config.alternateItemPopUpHeader,isAlternateItems:true});
            }
            this.setState({
                isAddDisabled: false
            });
        // }

    };

    // private readonly _inventoryLabel = (product?: IFullProduct, productAvailability?: IProductInventoryInformation[]): string | undefined => {
    //     if (productAvailability) {
    //         const productInventoryInformation = this._getProductInventory(product, productAvailability);
    //         if (productInventoryInformation) {
    //             return productInventoryInformation.StockLevelLabel;
    //         }
    //     }

    //     return undefined;
    // };

    // private _getProductInventory(product?: IFullProduct, productInventoryCollection?: IProductInventoryInformation[] | undefined): IProductInventoryInformation | undefined {
    //     if (productInventoryCollection && productInventoryCollection.length > 0) {
    //         for (const productResult of productInventoryCollection) {
    //             if (product && product.ProductDetails.RecordId === productResult.ProductAvailableQuantity.ProductId) {
    //                 return productResult;
    //             }
    //         }
    //     }
    //     return undefined;
    // }

    private readonly removeHandler = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({
            isRemoveDisabled: true
        });

        this.props.onRemoveSelection();
    };

}
